/* eslint-disable camelcase */
import { useContext, useState } from "react"
import Header from "../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Chip } from "@mui/material"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../../contexts/GlobalContext"
import { useMutation, api } from "../../../api"
import getIconComponent from "../../utils/getIconComponent"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useQueryClient } from "@tanstack/react-query"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../../utils/reactTableActions/exportToolbarActions"
import ModalImportCSV from "../../../components/CustomCSVImportButton"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const DelegadosCordinadores = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)
  const [openModal, setOpenModal] = useState(false)

  // ========================================================
  // LECTURA ROBUSTA DE PERMISOS (FALLBACK A LOCALSTORAGE)
  // ========================================================
  let barraAcciones = selectedMenuInfo?.data?.barraAcciones

  if (!barraAcciones || barraAcciones.length === 0) {
    try {
      const menuAccionesRaw = localStorage.getItem("menuAcciones")
      if (menuAccionesRaw) {
        const menuAcciones = JSON.parse(menuAccionesRaw)
        // Buscamos la ruta exacta del módulo de Delegados y Coordinadores
        const currentMenu = menuAcciones["ControlElectoral/DelegadosCordinadores"]
        if (currentMenu && currentMenu.barraAcciones) {
          barraAcciones = currentMenu.barraAcciones
        }
      }
    } catch (error) {
      console.error("Error leyendo permisos desde localStorage:", error)
    }
  }

  // ========================================================
  // MUTACIÓN PARA ELIMINAR REGISTRO
  // ========================================================
  const { mutateAsync: SaveEliminacionDelegado, isPending: isDeletingDelegado } = useMutation({
    queryKey: ["isDeletingDelegado"],
    fn: async (data) => {
      // Apunta al endpoint de Python para eliminar personal (creado en rutas_maestros.py)
      const response = await api.post("/ControlElectoral/eliminarDelegado", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["DelegadosCordinadores"] })
    },
  })

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Catálogo de Delegados y Coordinadores</b>
        </div>

        <CustomBackdrop isLoading={isDeletingDelegado} />

        <Box className={StyledRoot}>
          {/* ======================================================== */}
          {/* MODAL DE IMPORTACIÓN CSV                                 */}
          {/* ======================================================== */}
          <ModalImportCSV
            open={openModal}
            onClose={() => setOpenModal(false)}
            templateFileName="PlantillaCreacionPersonal.csv"
            fieldConfigs={{
              del_codigo: { required: true, key: true },
              reci_codigo: { required: true },
              del_nombre: { required: true },
              del_telefono: { required: false }, // Teléfono opcional
              del_rol: { required: true }, // DELEGADO o COORDINADOR
            }}
            maxFileSize={10 * 1024 * 1024}
            validateEndpoint="/ControlElectoral/validarDelegadosIMP"
            insertEndpoint="/ControlElectoral/insertarDelegadosIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["DelegadosCordinadores"] })}
          />

          {/* ======================================================== */}
          {/* TABLA PRINCIPAL DEL MÓDULO                               */}
          {/* ======================================================== */}
          <CustomConditionalActionsTableServer
            endpoint="/ControlElectoral/getAllDelegados"
            errorMsgFilterSearch="Error al cargar el personal logístico"
            queryKeyModal="DelegadosCordinadores"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              const editarAction = barraAcciones?.find((action) => action?.acccaption === "EDITAR")
              const eliminarAction = barraAcciones?.find((action) => action?.acccaption === "ELIMINAR")

              const actions = [
                {
                  label: editarAction?.acccaption,
                  key: editarAction?.acccaption,
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) => {
                    navigate("editar", { state: row.original })
                  },
                },
                {
                  label: eliminarAction?.acccaption,
                  key: eliminarAction?.acccaption,
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: async (row) => {
                    try {
                      // Se envía la PK correcta: del_codigo (Cédula o ID)
                      await SaveEliminacionDelegado({
                        del_codigo: row.original.del_codigo,
                      })
                    } catch (err) {
                      console.error("Error eliminando el registro de personal:", err)
                    }
                  },
                },
              ]

              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = barraAcciones?.find((action) => action?.acccaption === "CREAR")
              const exportarAction = barraAcciones?.find((action) => action?.acccaption === "EXPORTAR")
              const importarAction = barraAcciones?.find((action) => action?.acccaption === "IMPORTAR")

              const toolbarActions = [
                {
                  label: crearAction?.acccaption,
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => {
                    navigate("crear")
                  },
                },
                {
                  type: "dropdown",
                  label: exportarAction?.acccaption,
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            "Reporte de Personal Electoral",
                            `Reporte de Personal Electoral ${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          "Reporte de Personal Electoral",
                          `Reporte de Personal Electoral ${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) => {
                        handleAllExportDataCSV(data, `Reporte de Personal Electoral ${new Date().toLocaleString()}`)
                      },
                    },
                  ],
                },
                {
                  label: importarAction?.acccaption,
                  key: importarAction?.acccaption,
                  icon: getIconComponent(importarAction?.accnameicono, importarAction?.acctipoico),
                  onClick: () => {
                    setOpenModal(true)
                  },
                },
              ]

              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "del_codigo",
                header: "Identificación / Cédula",
                size: 160,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "del_nombre",
                header: "Nombre del Personal",
                size: 250,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "reci_codigo",
                header: "Recinto Asignado",
                size: 150,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "del_telefono",
                header: "Teléfono",
                size: 130,
                Cell: ({ cell }) => <span>{cell.getValue() || "N/A"}</span>,
              },
              {
                accessorKey: "del_rol",
                header: "Rol Logístico",
                size: 150,
                Cell: ({ cell }) => {
                  const rol = cell.getValue()
                  return (
                    <Chip
                      label={rol}
                      size="small"
                      color={rol === "COORDINADOR" ? "primary" : "default"}
                      sx={{ fontWeight: "bold" }}
                    />
                  )
                },
              },
              {
                accessorKey: "del_asistencia",
                header: "Check-in (AM)",
                size: 130,
                Cell: ({ cell }) => {
                  const asistencia = cell.getValue()
                  // Maneja bit (1/0) o booleano (true/false)
                  const asiste = asistencia === 1 || asistencia === true || asistencia === "1"
                  return (
                    <span style={{ color: asiste ? "green" : "red", fontWeight: "bold" }}>
                      {asiste ? "ASISTIÓ" : "PENDIENTE"}
                    </span>
                  )
                },
              },
              {
                accessorKey: "aud_fecisys",
                header: "Fecha de Registro",
                size: 160,
                Cell: ({ cell }) => {
                  const value = cell.getValue()
                  return <span>{normalFormatDate(value)}</span>
                },
              },
              {
                accessorKey: "aud_usuisys",
                header: "Registrado Por",
                size: 150,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default DelegadosCordinadores
